import { Chip, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { ContractDto, ContractSearchPaginatedDto, ContractType } from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import { ContractAutocompleteOption, contractToOption } from "./ContractAutocomplete";

export interface ContractsAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<ContractDto> {
  searchFilters?: {
    vehicleId?: string;
  } & Partial<Omit<ContractSearchPaginatedDto, "search" | "includeIds">>;
}

export default function ContractsAutocomplete({
  isPreload = true,
  searchFilters,
  ...otherProps
}: ContractsAutocompleteProps) {
  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={contractToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.contractsApi.apiV1ContractsSearchPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          contractSearchPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          contractSearchPaginatedDto: {
            ...params.contractSearchPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Contracts'
      placeholder='Search for contracts...'
      renderOption={(props, option: ContractAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='contract' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography component='div' variant='body1'>
                    {option.title}
                  </Typography>

                  {option.subtitle && (
                    <Typography component='div' variant='body1'>
                      {option.subtitle}
                    </Typography>
                  )}

                  {option.data && (
                    <Typography component='div' variant='body1'>
                      {option.data && option.data.type !== ContractType.None && (
                        <Chip
                          size='small'
                          color='default'
                          variant={"outlined"}
                          label={
                            <InlineApiEnumValue type='ContractType' value={option.data.type} />
                          }
                          sx={{ mr: 1 }}
                        />
                      )}
                      {option.data && (
                        <Chip
                          size='small'
                          color='default'
                          variant={"outlined"}
                          label={
                            <InlineApiEnumValue
                              type='ContractStage'
                              value={option.data.stageHistory?.stage}
                            />
                          }
                          sx={{ mr: 1 }}
                        />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
